import { BulletList } from '@hendrx/icons/BulletList';
import { DropdownArrow } from '@hendrx/icons/DropdownArrow';
import { NumberedList } from '@hendrx/icons/NumberedList';
import { Box, Flex, SelectProps } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import { Editor } from '@tiptap/react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useContentLanguage } from 'shared/components/providers/ContentLanguageProvider';
import { Select } from 'shared/components/selects/Select';
import { getFlipStyle } from 'shared/utils/locale.utils';

import { BUTTONS_GAP_PX } from '../RichTextToolbar.consts';
import { useToolbarSelectStyles } from '../useToolbarSelectStyles';

const listTypeIcon = {
  bulletList: BulletList,
  orderedList: NumberedList
} as const;

const listTypeComponent = {
  bulletList: RichTextEditor.BulletList,
  orderedList: RichTextEditor.OrderedList
} as const;

const listTypeOptions = [
  { value: 'bulletList', label: '' },
  { value: 'orderedList', label: '' }
] as const;

export type ListTypeSelectProps = {
  editor: Editor | null;
  isSelectView?: boolean;
};

export function ListTypeSelect(props: ListTypeSelectProps) {
  const { isSelectView } = props;
  const { classes, style } = useToolbarSelectStyles({ maxWidth: 60 });
  const { t } = useTranslation('widgets.listTypeSelect');
  const { editor } = props;
  const { contentDirection } = useContentLanguage();

  const currentListType = listTypeOptions.find(listType => editor?.isActive(listType.value)) ?? null;

  const Icon = listTypeIcon[currentListType?.value ?? 'bulletList'];

  return isSelectView ? (
    <Select
      title={t('title') ?? ''}
      style={style}
      styles={{ dropdown: style }}
      classNames={{
        root: classes.selectRoot,
        option: classes.selectItem,
        input: classes.selectInput,
        section: classes.selectIcon,
        label: classes.selectLabel
      }}
      data={listTypeOptions}
      renderOption={renderListOption}
      label={
        <Box data-active={currentListType !== null}>
          <Icon style={getFlipStyle(contentDirection)} />
        </Box>
      }
      variant="unstyled"
      leftSection={<DropdownArrow />}
      leftSectionWidth={16}
      rightSection={<></>}
      rightSectionWidth={'0px'}
    />
  ) : (
    <Flex direction="row" dir="ltr" gap={BUTTONS_GAP_PX}>
      {listTypeOptions.map(listTypeData => (
        <ListTypeOption key={listTypeData.value} value={listTypeData.value} />
      ))}
    </Flex>
  );
}

type ListTypeOptionProps = {
  value: (typeof listTypeOptions)[number]['value'];
};

const renderListOption: SelectProps['renderOption'] = ({ option }) => (
  <ListTypeOption value={option.value as ListTypeOptionProps['value']} />
);

const ListTypeOption = forwardRef<HTMLButtonElement, ListTypeOptionProps>(function ListTypeOption(props, ref) {
  const { value } = props;
  const { contentDirection } = useContentLanguage();

  const { t } = useTranslation('widgets.listTypeSelect');

  const Component = listTypeComponent[value];
  const Icon = listTypeIcon[value];

  return (
    <Component
      icon={() => <Icon />}
      title={t(value) ?? ''}
      ref={ref}
      m={0}
      w="100%"
      style={getFlipStyle(contentDirection)}
      bg="transparent"
    />
  );
});
